/**
 * O documento na lista do exame eletrônico (`ExameEletronicoCcbOut`), reexportado por
 * `exame_eletronico.ts`. Módulo próprio desde a E101.F5, quando a leitura por imagem entrou e o
 * cliente do exame passou do teto de linhas.
 */
import type { EstadoDaLeituraPorImagem, MotivoDaFalhaDaLeitura } from '@/lib/case_source_documents';
import type {
  ExameEletronicoRecordData,
  GrauConvergencia,
  TipoDaPeca,
} from '@/lib/exame_eletronico';

export interface ExameEletronicoCcbData {
  source_document_id: string;
  ccb_number: string | null;
  /** E90.F6 — o nome dado pelo perito; vence o tipo e o arquivo na tela e nos documentos. */
  nome_do_documento?: string | null;
  /** E78.F1 — nome do arquivo enviado; identifica a peça sem número de CCB. */
  original_filename: string;
  documento_content_sha256: string | null;
  /** E78.F2 — o que a peça declara; existe antes de qualquer verificação. */
  codigo_declarado?: string | null;
  observacoes_peca?: string | null;
  /** E84.F1 — deduzido do rótulo do hash do comprovante; nulo = ninguém deduziu. */
  tipo_da_peca?: TipoDaPeca | null;
  /**
   * E106.F3.C11 — a letra da peça pela regra do documento emitido (o comprovante separado
   * entra na sequência; a fotografia fica sem letra e é identificada pela página). Ausente
   * só em payload anterior ao campo: aí a tela cai na posição da lista.
   */
  letra?: string | null;
  /** E101.F5 — a leitura por imagem (nulo = documento com texto) e o motivo, só em `falhou`. */
  leitura_por_imagem?: EstadoDaLeituraPorImagem | null;
  leitura_por_imagem_motivo?: MotivoDaFalhaDaLeitura | null;
  /**
   * Redação padrão do grau, já preenchida com os dados desta CCB — uma entrada
   * por grau. Vem no payload (e não por uma rota consultada a cada troca) porque
   * a mesa deixa o perito reclassificar; vazio enquanto o exame não rodou.
   */
  conclusoes_sugeridas?: Partial<Record<GrauConvergencia, string>>;
  exame: ExameEletronicoRecordData | null;
  /**
   * E162.F2.T02 (ADR-0111 §2.1) — a régua do servidor: só a CCB (e a peça sem tipo) pede a
   * conclusão do perito; o apoio verificado está concluído pela verificação. Ausente só em
   * payload anterior ao campo: aí toda peça exige, como antes.
   */
  exige_conclusao?: boolean;
  concluida?: boolean;
  /**
   * E177.F6 (GUS-253, ADR-0130) — as CCBs dos outros casos da mesma pasta e processo que têm os
   * mesmos bytes (o documento compartilhado). Ausente ou vazio: só desta CCB.
   */
  compartilhada_com?: string[];
}

/** A peça pede a conclusão do perito (a CCB, a peça sem tipo). */
export function exigeConclusao(peca: ExameEletronicoCcbData): boolean {
  return peca.exige_conclusao !== false;
}

/** Concluída pela régua do servidor: a CCB pelo perito, o apoio pela verificação. */
export function pecaConcluida(peca: ExameEletronicoCcbData): boolean {
  return peca.concluida ?? peca.exame?.status === 'concluido';
}
